import React, { useRef } from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { fundsManagementTracker } from '@mobi/account/Common/Analytics'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { track } from '@mobi/account/Utils'
import { CustomerServiceCentre } from '@mobi/component-library/Base/Link'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { font, spacing } from '@mobi/component-library/Foundations'
import { CopyField } from '@mobi/component-library/Selection&Input/CopyField'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { selectDepositFlow } from '../../../Store'
import type { PanelProps } from '../../types'
import { DepositLimitRow } from '../DepositLimitRow'

type Props = Pick<PanelProps, 'accountNumber' | 'initialData'> & {
  isPreCommitmentLimitSet: boolean
  remainingDepositAmount: number
  remainingDepositTransactions: number
}

const minBPayDeposit = 20
const maxBPayDeposit = 500_000
const formattedMaxBPayDeposit: string = maxBPayDeposit.toLocaleString()

export const BPayPanel = ({
  accountNumber,
  initialData,
  isPreCommitmentLimitSet,
  remainingDepositAmount,
  remainingDepositTransactions,
}: Props) => {
  const isDepositValueVolumeLimitsEnabled = useHostContextFeature(
    FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key
  )
  const flow = useSelector(selectDepositFlow)
  const { depositLimits } = initialData
  const containerRef = useRef<HTMLDivElement>(null)

  const isOldAccountNumbers = accountNumber && accountNumber <= 2176659

  return (
    <div ref={containerRef}>
      <BPayPanelStyled>
        <dt>Biller Code</dt>
        <dd>
          <CopyField
            value='1214'
            onCopied={() => {
              track('deposit_copy_content', {
                accountNumber,
                paymentMethod: 'BPay',
                copiedContent: 'Biller Code',
                copiedLocation: 'BPay',
                depositVariant: flow,
              })
            }}
          />
        </dd>
        <dt>Reference</dt>

        <dd>
          {isOldAccountNumbers ? (
            <CustomerServiceCentre
              suffixText='on your BPay reference.'
              onClickPhoneNumber={() =>
                fundsManagementTracker({ interaction_type: 'Need Assistance', location: 'Deposit' })
              }
            />
          ) : (
            <CopyField
              value={accountNumber?.toString() ?? ''}
              onCopied={() => {
                track('deposit_copy_content', {
                  accountNumber,
                  paymentMethod: 'BPay',
                  copiedContent: 'Account Number',
                  copiedLocation: 'BPay',
                  depositVariant: flow,
                })
              }}
            />
          )}
        </dd>
        {initialData.depositLimits.maximum >= minBPayDeposit && (
          <>
            <dt>Minimum Deposit</dt>
            <dd style={{ fontVariant: 'tabular-nums' }}>${minBPayDeposit}</dd>
          </>
        )}
        {(isDepositValueVolumeLimitsEnabled ||
          !isPreCommitmentLimitSet ||
          initialData.depositLimits.maximum > maxBPayDeposit) && (
          <DepositLimitRow
            depositLimits={depositLimits}
            remainingDepositAmount={Math.min(remainingDepositAmount, maxBPayDeposit)}
            remainingDepositTransactions={remainingDepositTransactions}
            showDepositLimitLink={flow !== 'quick-deposit'}
            fallback={
              !isDepositValueVolumeLimitsEnabled ? `$${formattedMaxBPayDeposit}` : undefined
            }
          />
        )}
      </BPayPanelStyled>

      <InfoBox role='status' color='grey' size='md'>
        BPay deposits are fee-free and usually reach your TABtouch account within 1-3 days.
      </InfoBox>
    </div>
  )
}

BPayPanel.displayName = 'BPayPanel'

const BPayPanelStyled = styled.dl({
  alignItems: 'center',
  display: 'grid',
  fontFamily: font.family.primary,
  gap: spacing.sm,
  gridAutoRows: '5rem',
  gridTemplateColumns: 'minmax(0, max-content) 1fr',
  margin: 0,
  marginBottom: spacing.md,
  ...font.size.lg,

  dt: {
    fontWeight: font.weight.semibold,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.smx2,
  },

  dd: {
    margin: 0,
  },
})
